'use client'

import { type Driver, driver } from 'driver.js'
import { ArrowDownIcon, ArrowUpIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { firstVisible, measureClip, scrollAncestors } from '@/features/onboarding/lib/tour-target'
import { preferredTourSide, type Rect } from '@/features/onboarding/lib/welcome-tour'
import { cn } from '@/lib/utils'

/** Room around the target inside the cut-out; the ring sits on its edge. */
const STAGE_PAD = 6
const STAGE_RADIUS = 8
const STAGE_DURATION_MS = 300
/** Card size to plan the side with before it has rendered once. */
const FALLBACK_CARD = { width: 420, height: 280 }

export interface TourTarget {
  element: HTMLElement
  rect: Rect
  /** Set when a scroll area inside the widget or page cuts part of the target off. */
  clip?: TargetClip
}

export interface TargetClip {
  /** How much of the target shows inside the scroll areas around it, 0–1. */
  shown: number
  /** The part that shows. */
  visibleRect: Rect
  /** The scroll area it's cut off by, its box on screen, and which way reveals the target. */
  container: HTMLElement
  containerRect: Rect
  direction: 'up' | 'down'
}

/**
 * A target counts as in view from this share showing; it's tucked away again only below
 * SHOWN_LOST, so scrolling right at the line doesn't flicker between the two.
 */
const SHOWN_ENOUGH = 0.6
/** The in-ring scroll hint's height (text-xs + py-1 + border), to sit it inside the edge. */
const SCROLL_HINT_HEIGHT = 26
const SHOWN_LOST = 0.4

/**
 * The workspace dimmed except one real target, with our card in a popover beside it — a thin
 * wrapper over driver.js (overlay, animated cut-out, popover placement and arrow), themed in
 * `app/globals.css` (`.ox-tour-popover`, `.driver-overlay`). Shared by the welcome tour and the
 * in-place widget tutorial.
 *
 * The page stays live: the overlay takes no clicks, and driver's keyboard handling (Esc/arrows,
 * the Tab trap) is off, so the user can use the real control — and anything it opens — while
 * the card explains it. Callers own the keys. With no target the card is centred.
 */
export function TourSpotlight({
  target,
  placeBeside,
  stepKey,
  labelledBy,
  aboveSheets = false,
  showPopover = true,
  className,
  children,
}: {
  target: TourTarget | null
  /** What the card must not cover (the whole widget, when the target is one of its controls). */
  placeBeside?: Rect | null
  /** Changes when the step does: the card re-enters and the cut-out moves. */
  stepKey: string
  /** Id of the card's heading, for the popover's dialog role. */
  labelledBy: string
  /**
   * Lift the whole tour over the app's sheets and dialogs (z-50), for a tour whose targets
   * are inside one (the order form sheet). Otherwise it sits under them, so anything a step
   * opens stays on top.
   */
  aboveSheets?: boolean
  /** Ring and cut-out only, for a brief highlight without a tutorial card. */
  showPopover?: boolean
  className?: string
  children: ReactNode
}) {
  // A stable node React renders the card into; driver makes a new popover per step and we
  // move this node into it, so the card is already laid out when driver measures it.
  const [host] = useState(() => {
    const el = document.createElement('div')
    el.className = 'ox-tour-card-host'
    // The driver wrapper includes transparent placement space and an arrow. Only this real
    // card should catch input; otherwise an adjacent highlighted control can become unclickable.
    el.style.pointerEvents = 'auto'
    return el
  })
  const t = useTranslations('tutorial')
  const driverRef = useRef<Driver | null>(null)

  // A control hidden down a widget's scroll area: point at the scroll area and say which way
  // to scroll, then at the control once enough of it shows (only the part that shows).
  const clip = target?.clip
  const tuckedRef = useRef<{ element: HTMLElement | null; tucked: boolean }>({
    element: null,
    tucked: false,
  })
  if (tuckedRef.current.element !== (target?.element ?? null)) {
    tuckedRef.current = {
      element: target?.element ?? null,
      tucked: !!clip && clip.shown < SHOWN_ENOUGH,
    }
  } else if (clip) {
    const { tucked } = tuckedRef.current
    if (tucked && clip.shown >= SHOWN_ENOUGH) tuckedRef.current.tucked = false
    else if (!tucked && clip.shown < SHOWN_LOST) tuckedRef.current.tucked = true
  } else tuckedRef.current.tucked = false
  const tucked = tuckedRef.current.tucked && clip ? clip : null
  const shown: TourTarget | null = tucked
    ? { element: tucked.container, rect: tucked.containerRect }
    : target && clip
      ? { element: target.element, rect: clip.visibleRect }
      : target

  const placeRef = useRef<Rect | null>(null)
  placeRef.current = placeBeside ?? shown?.rect ?? null
  const labelRef = useRef(labelledBy)
  labelRef.current = labelledBy
  const layerRef = useRef(aboveSheets)
  layerRef.current = aboveSheets

  useEffect(() => {
    const aria = createAriaGuard()
    const instance = driver({
      animate: !prefersReducedMotion(),
      duration: STAGE_DURATION_MS,
      stagePadding: STAGE_PAD,
      stageRadius: STAGE_RADIUS,
      popoverOffset: 12,
      popoverClass: 'ox-tour-popover',
      showButtons: [],
      allowClose: false,
      allowKeyboardControl: false,
      overlayClickBehavior: () => {},
      smoothScroll: true,
      onHighlightStarted: (element, _step, { state }) => {
        aria.hold(state.activeElement)
        aria.hold(element)
      },
      // Re-place once the cut-out has landed: the card is rendered mid-transition, and any
      // refresh until now measured the previous step's element.
      onHighlighted: () => {
        pinOverlay(instance, layerRef.current)
        instance.refresh()
      },
      onPopoverRender: (popover) => {
        pinPopover(popover.wrapper, layerRef.current)
        popover.wrapper.appendChild(host)
        popover.wrapper.setAttribute('aria-labelledby', labelRef.current)
        popover.wrapper.removeAttribute('aria-describedby')
      },
    })
    driverRef.current = instance
    const resize = new ResizeObserver(() => refreshSettled(instance))
    resize.observe(host)
    return () => {
      resize.disconnect()
      aria.hold(instance.getActiveElement())
      instance.destroy()
      aria.release()
      driverRef.current = null
    }
  }, [host])

  useEffect(() => {
    if (!aboveSheets) return
    document.body.classList.add('ox-tour-above-sheets')
    return () => document.body.classList.remove('ox-tour-above-sheets')
  }, [aboveSheets])

  const element = shown?.element ?? null
  useEffect(() => {
    const instance = driverRef.current
    if (!instance) return
    const place = placeRef.current
    const card = host.isConnected
      ? { width: host.offsetWidth, height: host.offsetHeight }
      : FALLBACK_CARD
    instance.highlight({
      element: element ?? undefined,
      popover: showPopover
        ? {
            side: place
              ? preferredTourSide(
                  place,
                  { width: window.innerWidth, height: window.innerHeight },
                  card
                )
              : undefined,
            align: 'center',
          }
        : undefined,
    })
    releaseTabTrap(instance)
    // driver creates the overlay on its first animation frame, so pin it once that's run too.
    pinOverlay(instance, layerRef.current)
    const frame = requestAnimationFrame(() => pinOverlay(instance, layerRef.current))
    return () => cancelAnimationFrame(frame)
  }, [element, stepKey, host, showPopover])

  // Widgets move and resize under the tour; driver only re-measures on window resize/scroll.
  const rectKey = shown ? rectToKey(shown.rect) : ''
  useEffect(() => {
    if (driverRef.current) refreshSettled(driverRef.current)
  }, [rectKey])

  return (
    <>
      {createPortal(
        <div className={cn('menu-surface', className)}>
          {children}
          {/* The visible hint sits in the ring (below); this is its screen-reader twin. */}
          <p aria-live="polite" className="sr-only">
            {tucked ? t(tucked.direction === 'down' ? 'scrollHint.down' : 'scrollHint.up') : ''}
          </p>
        </div>,
        host
      )}
      {shown
        ? createPortal(
            <span
              aria-hidden
              className="border-ox-cyan ox-tour-ring pointer-events-none fixed z-[41] rounded-lg border-2 transition-[left,top,width,height] duration-300 ease-out"
              style={{
                left: shown.rect.left - STAGE_PAD,
                top: shown.rect.top - STAGE_PAD,
                width: shown.rect.width + STAGE_PAD * 2,
                height: shown.rect.height + STAGE_PAD * 2,
              }}
            />,
            document.body
          )
        : null}
      {tucked && shown
        ? createPortal(
            // Inside the ringed scroll area, at the edge to scroll toward. Outside the ring's
            // own pulse so it doesn't blink, and click/scroll-through so the wheel still
            // scrolls the widget under it.
            <span
              aria-hidden
              className="bg-popover text-ox-cyan border-ox-cyan/40 pointer-events-none fixed flex -translate-x-1/2 items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium whitespace-nowrap transition-[left,top] duration-300 ease-out"
              style={{
                left: shown.rect.left + shown.rect.width / 2,
                top:
                  tucked.direction === 'down'
                    ? shown.rect.top + shown.rect.height - SCROLL_HINT_HEIGHT - 8
                    : shown.rect.top + 8,
                zIndex: aboveSheets ? 56 : 42,
              }}
            >
              {tucked.direction === 'down' ? (
                <ArrowDownIcon className="size-3.5" />
              ) : (
                <ArrowUpIcon className="size-3.5" />
              )}
              {t(tucked.direction === 'down' ? 'scrollHint.down' : 'scrollHint.up')}
            </span>,
            document.body
          )
        : null}
    </>
  )
}

/**
 * driver.js marks the highlighted element `aria-haspopup="dialog"` / `aria-expanded` /
 * `aria-controls`, then strips all three when it moves on — clobbering a Radix trigger's own
 * values. Hold each element's real values as driver reaches it and put them back right after.
 */
function createAriaGuard() {
  const ATTRS = ['aria-haspopup', 'aria-expanded', 'aria-controls'] as const
  const held = new Map<Element, (string | null)[]>()
  let scheduled = false
  const release = () => {
    scheduled = false
    held.forEach((values, el) =>
      ATTRS.forEach((attr, i) => {
        const value = values[i]
        if (value === null) el.removeAttribute(attr)
        else el.setAttribute(attr, value)
      })
    )
    held.clear()
  }
  return {
    hold(el: Element | undefined) {
      if (!el || el.id === 'driver-dummy-element' || held.has(el)) return
      held.set(
        el,
        ATTRS.map((attr) => el.getAttribute(attr))
      )
      if (!scheduled) {
        scheduled = true
        // driver edits the attributes synchronously after its hook returns.
        queueMicrotask(release)
      }
    },
    release,
  }
}

/**
 * Re-measure, unless a step change is still animating: driver re-places against the element it
 * last *finished* highlighting, so a refresh now would snap the card and cut-out back to the
 * previous step. `onHighlighted` refreshes when it lands.
 */
function refreshSettled(instance: Driver) {
  if (!instance.getState('__transitionCallback')) instance.refresh()
}

/**
 * The overlay's layer and click-through, set inline as well as in globals.css. driver's own
 * defaults (z-index 10000, catching every click) cover the whole app, so if the stylesheet is
 * ever missing — a stale dev build served it without these rules once — the app must still be
 * usable, not blacked out under an overlay it can't click through.
 */
function pinOverlay(instance: Driver, aboveSheets: boolean) {
  const svg = instance.getState('__overlaySvg') as SVGSVGElement | undefined
  if (!svg) return
  svg.style.zIndex = aboveSheets ? '55' : '40'
  svg.style.pointerEvents = 'none'
  svg.querySelectorAll('path').forEach((path) => (path.style.pointerEvents = 'none'))
}

/** The popover's position and layer, inline for the same reason as `pinOverlay`. */
function pinPopover(wrapper: HTMLElement, aboveSheets: boolean) {
  wrapper.style.position = 'fixed'
  wrapper.style.zIndex = aboveSheets ? '57' : '45'
  wrapper.style.pointerEvents = 'none'
}

/** driver traps Tab inside the popover + target on `window`; the tour is non-modal, so undo it. */
function releaseTabTrap(instance: Driver) {
  const events = instance.getState('__events') as
    | { onKeydown?: (event: KeyboardEvent) => void }
    | undefined
  if (events?.onKeydown) window.removeEventListener('keydown', events.onKeydown)
}

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

function rectToKey(r: Rect) {
  return `${r.left}|${r.top}|${r.width}|${r.height}`
}

/** The first visible match for `selector` and its box, re-measured every frame. */
export function useTourTarget(selector: string | null): TourTarget | null {
  const [target, setTarget] = useState<TourTarget | null>(null)
  useEffect(() => {
    if (!selector) {
      setTarget(null)
      return
    }
    let frame = 0
    // `null` = nothing committed for this selector yet, so the first frame always commits —
    // even "not found". Otherwise a step whose control isn't rendered kept pointing at the
    // previous step's element (both read as `undefined` → no update).
    let lastEl: HTMLElement | undefined | null = null
    let lastKey = ''
    let scrollers: HTMLElement[] = []
    const tick = () => {
      const el = firstVisible(document, selector)
      if (el !== lastEl) scrollers = el ? scrollAncestors(el) : []
      const r = el?.getBoundingClientRect()
      const rect = r ? { left: r.left, top: r.top, width: r.width, height: r.height } : null
      const clip = el && rect ? measureClip(rect, scrollers) : undefined
      const key = rect
        ? `${rectToKey(rect)}|${clip ? `${clip.shown.toFixed(2)}|${rectToKey(clip.containerRect)}` : ''}`
        : ''
      if (el !== lastEl || key !== lastKey) {
        lastEl = el
        lastKey = key
        setTarget(el && rect ? { element: el, rect, clip } : null)
      }
      frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [selector])
  return target
}
